export type DiscordUser = {
  id: string;
  username: string;
  global_name?: string | null;
  avatar?: string | null;
};

export type ServerSummary = {
  id: string;
  name: string;
  tier: string;
  recap_channel_id: string | null;
  recap_time: string | null;
  personality: string;
  flame_enabled: boolean;
  voice_profile: string | null;
  emoji_bank: string[];
  has_recap: boolean;
  bot_display_name: string | null;
  bot_avatar_url: string | null;
  bot_status: string | null;
  bot_description: string | null;
  bot_banner: string | null;
  custom_personality: string | null;
  mention_behavior: string;
  icon_url: string | null;
  // behavior toggles (stored in config jsonb, default true)
  allow_profanity: boolean;
  allow_organic_replies: boolean;
  allow_emoji_reactions: boolean;
  allow_indexing: boolean;
  allow_recaps: boolean;
  allow_roasting: boolean;
  lock_emoji_bank: boolean;
  channel_filter_mode: string;
  channel_filter_ids: string[];
  system_prompt: string | null;
};

export type ManageableGuild = {
  id: string;
  name: string;
  icon_url: string | null;
};

export type Overview = {
  messageCount: number;
  loreCount: number;
  optedOutCount: number;
  auditCountToday: number;
  lastIndexedAt: string | null;
};

export type LoreEntry = {
  id: string;
  title: string;
  summary: string;
  category: string | null;
  pinned: boolean;
  created_at: string;
};

export type AuditEvent = {
  id: string;
  actor_discord_id: string | null;
  action: string;
  target: string | null;
  created_at: string;
};

export type ServerReport = {
  id: string;
  type: string;
  headline: string;
  body: string;
  lore_entry_id: string | null;
  dismissed: boolean;
  created_at: string;
};

export type ServerDetail = {
  server: ServerSummary;
  overview: Overview;
  lore: LoreEntry[];
  audit: AuditEvent[];
  reports: ServerReport[];
};

export type MeResponse = {
  user: DiscordUser;
  isAdmin: boolean;
  servers: ServerSummary[];
  addableGuilds: ManageableGuild[];
};

export type SettingsPatch = Partial<{
  personality: string;
  flame_enabled: boolean;
  recap_channel_id: string | null;
  recap_time: string | null;
  emoji_bank: string[];
  bot_display_name: string | null;
  bot_avatar_url: string | null;
  bot_status: string | null;
  bot_description: string | null;
  bot_banner: string | null;
  custom_personality: string | null;
  mention_behavior: string;
  allow_profanity: boolean;
  allow_organic_replies: boolean;
  allow_emoji_reactions: boolean;
  allow_indexing: boolean;
  allow_recaps: boolean;
  allow_roasting: boolean;
  lock_emoji_bank: boolean;
  channel_filter_mode: string;
  channel_filter_ids: string[];
  system_prompt: string | null;
}>;

export type NewLoreEntry = {
  title: string;
  summary: string;
};

export type AskMessage = {
  role: "user" | "kodama";
  content: string;
};

const SESSION_KEY = "kodama_session";
const SESSION_COOKIE = "kodama_session";
const SHARED_DOMAIN = ".askkodama.com";
const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

function cookieSecure(): string {
  return location.protocol === "https:" ? "; Secure" : "";
}

export function getStoredToken(): string | null {
  try {
    // Primary: localStorage (on app.askkodama.com)
    const ls = localStorage.getItem(SESSION_KEY);
    if (ls) return ls;
  } catch { /* ignore */ }
  try {
    // Fallback: shared domain cookie (readable on askkodama.com too)
    const match = document.cookie
      .split("; ")
      .find((r) => r.startsWith(`${SESSION_COOKIE}=`));
    return match ? decodeURIComponent(match.split("=")[1]) : null;
  } catch {
    return null;
  }
}

export function storeToken(token: string): void {
  try {
    localStorage.setItem(SESSION_KEY, token);
  } catch { /* ignore */ }
  try {
    // Also store in a shared cookie so askkodama.com can read it
    document.cookie = `${SESSION_COOKIE}=${encodeURIComponent(token)}; Domain=${SHARED_DOMAIN}; Path=/; SameSite=Lax; Max-Age=2592000${cookieSecure()}`;
  } catch { /* ignore */ }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch { /* ignore */ }
  try {
    document.cookie = `${SESSION_COOKIE}=; Domain=${SHARED_DOMAIN}; Path=/; SameSite=Lax; Max-Age=0${cookieSecure()}`;
    document.cookie = `${SESSION_COOKIE}=; Path=/; SameSite=Lax; Max-Age=0${cookieSecure()}`;
  } catch { /* ignore */ }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message?: string,
  ) {
    super(message ?? `API ${status}`);
  }
}

export class AuthStartError extends Error {
  constructor() {
    super("Could not start Discord sign-in");
  }
}

function authStartError(): AuthStartError {
  window.dispatchEvent(new CustomEvent("kodama:auth-start-error"));
  return new AuthStartError();
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getStoredToken();
  const res = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: unknown;
    } | null;
    throw new ApiError(
      res.status,
      typeof body?.error === "string" ? body.error : undefined,
    );
  }

  return (await res.json()) as T;
}

/** Kick off Discord OAuth. The server returns the redirect URL as JSON. */
export async function initiateLogin(returnTo = "/servers"): Promise<void> {
  const res = await fetch(
    `${apiBase}/api/auth/login?return_to=${encodeURIComponent(returnTo)}`,
  ).catch(() => {
    throw authStartError();
  });

  if (!res.ok) throw authStartError();

  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw authStartError();
  }

  const data = (await res.json()) as { url?: unknown };
  if (typeof data.url !== "string" || !data.url.startsWith("https://")) {
    throw authStartError();
  }

  window.location.href = data.url;
}

/**
 * Exchange an OAuth code+state for a session token.
 * Called by the callback page after Discord redirects back.
 */
export async function exchangeOAuthCode(
  code: string,
  state: string,
): Promise<{ token: string; returnTo: string }> {
  const res = await fetch(`${apiBase}/api/auth/exchange`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, state }),
  });
  if (!res.ok) throw new ApiError(res.status);
  return (await res.json()) as { token: string; returnTo: string };
}

export async function getMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>("/api/me");
}

export async function getServer(serverId: string): Promise<ServerDetail> {
  return apiFetch<ServerDetail>(`/api/servers/${serverId}`);
}

export async function updateSettings(
  serverId: string,
  patch: SettingsPatch,
): Promise<{ server: ServerSummary | null }> {
  return apiFetch<{ server: ServerSummary | null }>(
    `/api/servers/${serverId}/settings`,
    {
      method: "PATCH",
      body: JSON.stringify(patch),
    },
  );
}

export async function createLore(
  serverId: string,
  payload: NewLoreEntry,
): Promise<{ lore: LoreEntry }> {
  return apiFetch<{ lore: LoreEntry }>(`/api/servers/${serverId}/lore`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function deleteLore(
  serverId: string,
  loreId: string,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(`/api/servers/${serverId}/lore/${loreId}`, {
    method: "DELETE",
  });
}

export async function askKodama(
  serverId: string,
  question: string,
  history: AskMessage[],
): Promise<{ answer: string }> {
  return apiFetch<{ answer: string }>(`/api/servers/${serverId}/ask`, {
    method: "POST",
    body: JSON.stringify({ question, history }),
  });
}

export async function dismissReport(
  serverId: string,
  reportId: string,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/api/servers/${serverId}/reports/${reportId}/dismiss`,
    { method: "POST" },
  );
}

export async function promoteReport(
  serverId: string,
  reportId: string,
): Promise<{ ok: true; lore: LoreEntry[] }> {
  return apiFetch<{ ok: true; lore: LoreEntry[] }>(
    `/api/servers/${serverId}/reports/${reportId}/promote`,
    { method: "POST" },
  );
}

export type GuildChannel = {
  id: string;
  name: string;
  type: number;
};

export async function fetchServerChannels(
  serverId: string,
): Promise<GuildChannel[]> {
  const res = await apiFetch<{ channels: GuildChannel[] }>(
    `/api/servers/${serverId}/channels`,
  );
  return res.channels;
}

export async function scanServerHistory(
  serverId: string,
): Promise<{ queued: true }> {
  return apiFetch<{ queued: true }>(`/api/servers/${serverId}/scan`, {
    method: "POST",
  });
}

export async function createCheckoutSession(
  tier: "index" | "chronicle",
): Promise<{ url: string }> {
  return apiFetch<{ url: string }>("/api/stripe/checkout", {
    method: "POST",
    body: JSON.stringify({ tier }),
  });
}

export async function createBillingPortalSession(): Promise<{ url: string }> {
  return apiFetch<{ url: string }>("/api/stripe/portal", {
    method: "POST",
  });
}

export async function logout(): Promise<void> {
  await apiFetch("/api/auth/logout", { method: "POST" });
  clearToken();
}

// ── Cross-domain auth hint cookie ─────────────────────────────────────────────
// A lightweight, non-sensitive cookie shared across askkodama.com and
// app.askkodama.com so the marketing site can reflect signed-in state without
// having access to the actual session token in localStorage.
// Value is just the display username — never the token itself.

const AUTH_HINT_COOKIE = "kodama_user";
const AUTH_HINT_DOMAIN = ".askkodama.com";

export function setAuthHintCookie(username: string): void {
  try {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${AUTH_HINT_COOKIE}=${encodeURIComponent(username)}; Domain=${AUTH_HINT_DOMAIN}; Path=/; SameSite=Lax; Max-Age=2592000${secure}`;
  } catch {
    // ignore — non-critical
  }
}

export function clearAuthHintCookie(): void {
  try {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    // Clear for both the shared domain and the current origin
    document.cookie = `${AUTH_HINT_COOKIE}=; Domain=${AUTH_HINT_DOMAIN}; Path=/; SameSite=Lax; Max-Age=0${secure}`;
    document.cookie = `${AUTH_HINT_COOKIE}=; Path=/; SameSite=Lax; Max-Age=0${secure}`;
  } catch {
    // ignore
  }
}

export function getAuthHintCookie(): string | null {
  try {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${AUTH_HINT_COOKIE}=`));
    return match ? decodeURIComponent(match.split("=")[1]) : null;
  } catch {
    return null;
  }
}

// ── Admin API ─────────────────────────────────────────────────────────────────

export type AdminServer = {
  id: string;
  name: string;
  tier: string;
  personality: string;
  icon_url: string | null;
  message_count: number;
  lore_count: number;
  opted_out_count: number;
  member_count: number;
  last_indexed_at: string | null;
  joined_at: string;
};

export type AdminStats = {
  totalServers: number;
  totalMessages: number;
  totalLore: number;
  totalMembers: number;
  totalOptedOut: number;
  activeSessions: number;
};


export async function getAdminStats(): Promise<AdminStats> {
  return apiFetch<AdminStats>("/api/admin/stats");
}

export async function getAdminServers(): Promise<{ servers: AdminServer[] }> {
  return apiFetch<{ servers: AdminServer[] }>("/api/admin/servers");
}
